import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface FornecedorItem {
  id: number;
  codigo: number | null;
  nome: string;
  razaoSocial: string;
  cnpj: string;
  cpf: string;
  ativo: boolean;
  telefone1: string;
  email1: string;
  cid: string;
  centroCusto: string;
  finStatusFornecedor: string;
  idFinStatusFornecedor: number;
}

interface Opcao {
  id: number;
  descricao: string;
}

@Component({
  selector: 'app-consultar-fornecedor',
  templateUrl: './consultar-fornecedor.component.html',
  styleUrls: ['./consultar-fornecedor.component.scss']
})
export class ConsultarFornecedorComponent implements OnInit {
  itens: FornecedorItem[] = [];
  itensPaginados: FornecedorItem[] = [];
  statusFornecedor: Opcao[] = [];

  filtroCodigo: number | null = null;
  filtroNome = '';
  filtroRazaoSocial = '';
  filtroCnpj = '';
  filtroCpf = '';
  filtroAtivo: string = '';
  filtroIdFinStatusFornecedor = 0;

  loading = false;
  paginaAtual = 1;
  itensPorPagina = 10;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.buscar();
  }

  get totalPaginas(): number {
    return Math.ceil(this.itens.length / this.itensPorPagina) || 1;
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinFornecedor/ConsultarOpcoes`).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        this.statusFornecedor = this.normalizarLista(data.statusfornecedor).map((x: any) => ({
          id: Number(x.id || 0),
          descricao: String(x.descricao || '')
        })).filter((x: Opcao) => x.id > 0);
      }
    });
  }

  buscar(): void {
    const params = new URLSearchParams();
    if (this.filtroCodigo && this.filtroCodigo > 0) params.set('codigo', String(this.filtroCodigo));
    if (this.filtroNome.trim()) params.set('nome', this.filtroNome.trim());
    if (this.filtroRazaoSocial.trim()) params.set('razaoSocial', this.filtroRazaoSocial.trim());
    if (this.filtroCnpj.trim()) params.set('cnpj', this.filtroCnpj.trim());
    if (this.filtroCpf.trim()) params.set('cpf', this.filtroCpf.trim());
    if (this.filtroAtivo === 'true') params.set('ativo', 'true');
    if (this.filtroAtivo === 'false') params.set('ativo', 'false');
    if (this.filtroIdFinStatusFornecedor > 0) params.set('idFinStatusFornecedor', String(this.filtroIdFinStatusFornecedor));

    const query = params.toString();
    const url = `${API}financeiro/FinFornecedor/ConsultarFinFornecedor${query ? `?${query}` : ''}`;

    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        const lista = this.extrairListaResponse(response);
        this.itens = lista.map((x: any) => {
          const item = this.util.converterParaLowercase(x || {});
          return {
            id: Number(item.id || 0),
            codigo: item.codigo ? Number(item.codigo) : null,
            nome: String(item.nome || ''),
            razaoSocial: String(item.razaosocial || ''),
            cnpj: String(item.cnpj || ''),
            cpf: String(item.cpf || ''),
            ativo: Boolean(item.ativo),
            telefone1: String(item.telefone1 || ''),
            email1: String(item.email1 || ''),
            cid: String(item.cid || ''),
            centroCusto: String(item.centrocusto || item.nomecentrocusto || item.centrodecusto || ''),
            finStatusFornecedor: String(item.finstatusfornecedor || ''),
            idFinStatusFornecedor: Number(item.idfinstatusfornecedor || 0)
          } as FornecedorItem;
        }).filter((x: FornecedorItem) => x.id > 0);
        this.paginaAtual = 1;
        this.atualizarPaginacao();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.itens = [];
        this.itensPaginados = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar fornecedores.');
      }
    });
  }

  limparFiltros(): void {
    this.filtroCodigo = null;
    this.filtroNome = '';
    this.filtroRazaoSocial = '';
    this.filtroCnpj = '';
    this.filtroCpf = '';
    this.filtroAtivo = '';
    this.filtroIdFinStatusFornecedor = 0;
    this.buscar();
  }

  novo(): void {
    this.router.navigate(['/cadastrar-fornecedor']);
  }

  editar(item: FornecedorItem): void {
    this.router.navigate(['/cadastrar-fornecedor'], { queryParams: { id: item.id } });
  }

  deletar(item: FornecedorItem): void {
    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/FinFornecedor/Deletar`, { id: item.id }).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível excluir.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Fornecedor excluído com sucesso!');
        this.buscar();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao excluir fornecedor.');
      }
    });
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.itensPaginados = this.itens.slice(inicio, fim);
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'nome')) return [data];
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.normalizarLista(atual);
  }
}
